import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import AdminPayoutStatusModal from './AdminPayoutStatusModal';

interface Transaction {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  transaction_type: 'CREDIT' | 'DEBIT' | 'wallet_credit' | 'payout_debit' | 'escrow_release' | 'WALLET_CREDIT' | 'WALLET_DEBIT' | 'wallet_debit';
  amount: number;
  commission_amount: number;
  description: string;
  reference_id: string;
  status: string;
  payment_details?: any;
  recipient_details?: any;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  created_by_email?: string;
  created_by_role?: string;
  created_by?: number;
  user_wallet_balance?: number;
  balance_before?: number;
  balance_after?: number;
  manager_wallet_balance?: number;
  manager_balance_at_transaction?: number;
}

interface Payout {
  id: number;
  merchant_id: number;
  merchant_reference_number: string;
  viyona_transaction_id?: string;
  account_name: string;
  account_number: string;
  amount: number;
  net_amount?: number;
  transaction_fees_percentage?: number;
  transaction_fee_amount?: number;
  ifsc_code: string;
  bank_name: string;
  bank_branch?: string;
  transfer_type: 'IMPS' | 'NEFT' | 'RTGS';
  customer_email?: string;
  customer_phone?: string;
  remark?: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'COMPLETED';
  error_message?: string;
  bank_reference_number?: string;
  transaction_date?: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  current_wallet_balance?: number;
  response_data?: string | object | null;
  payout_source?: string | null;
}

interface Refund {
  id: number;
  user_id: number;
  amount: number;
  reference_id: string;
  description: string;
  created_at: string;
  merchant_name: string;
  merchant_email: string;
  merchant_reference_number: string | null;
  payout_id: number | null;
}

/** Format snake_case key as readable label (uppercase for modal labels) */
function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const getStatusColor = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED' || s === 'COMPLETED') return '#10B981';
  if (s === 'FAILED' || s === 'ERROR') return '#EF4444';
  if (s === 'PENDING' || s === 'PROCESSING') return '#F59E0B';
  return '#6B7280';
};

/** Response Message modal content in Payout Status style: two columns, card detail items, status badge */
function ResponseMessageModalContent({
  data,
  onClose,
}: {
  data: string | object | null;
  onClose: () => void;
}) {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (data == null) {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Response Message</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', margin: 0 }}>No data</p>
          </div>
          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (typeof data === 'string') {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Response Message</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '20px' }}>
            <div className="admin-payout-status-section">
              <div className="admin-payout-status-section-header">
                <span className="section-icon">📄</span>
                <h3>Message</h3>
              </div>
              <div className="admin-payout-status-section-body">
                <p style={{ margin: 0, color: '#1f2937', fontSize: '14px' }}>{data || '—'}</p>
              </div>
            </div>
          </div>
          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  const obj = data as Record<string, unknown>;
  const responseBody = (obj.response_body || obj.responseBody) as Record<string, unknown> | undefined;
  const resultStr = String(obj.result ?? obj.status ?? '');
  const statusColor = getStatusColor(resultStr);
  const topLevelKeys = Object.keys(obj).filter(k => k !== 'response_body' && k !== 'responseBody');

  const renderDetailValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return <span className="detail-value">—</span>;
    if (typeof value === 'object') return <span className="detail-value">{JSON.stringify(value)}</span>;
    const keyLower = key.toLowerCase();
    if (keyLower === 'amount') {
      const num = Number(value);
      return (
        <span className="detail-value amount">
          ₹{new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(isNaN(num) ? 0 : num)}
        </span>
      );
    }
    if (keyLower === 'response_status') return <span className="detail-value">{String(value)}</span>;
    const s = String(value);
    const isStatus = /^(SUCCESS|FAILED|PENDING|PROCESSING|COMPLETED|APPROVED|ERROR)$/i.test(s);
    if (isStatus) {
      return (
        <span className="detail-value" style={{ color: getStatusColor(s), fontWeight: 600 }}>
          {s}
        </span>
      );
    }
    const isMessage = keyLower.includes('message') || keyLower.includes('error');
    if (isMessage && /success|approved|submitted|transaction approved/i.test(s)) {
      return <span className="detail-value" style={{ color: '#059669', fontWeight: 600 }}>{s}</span>;
    }
    return <span className="detail-value">{s}</span>;
  };

  return (
    <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
      <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-payout-status-header">
          <h2 className="admin-payout-status-title">Response Message</h2>
          <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="admin-payout-status-content">
          {/* Left - Response Summary */}
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="status-dot" style={{ backgroundColor: statusColor }}></span>
              <h3>Response Summary</h3>
            </div>
            <div className="admin-payout-status-section-body response-message-summary-scroll">
              {resultStr && (
                <div className="status-badge-large" style={{ backgroundColor: statusColor }}>
                  {resultStr}
                </div>
              )}
              <div className="status-details-list">
                {topLevelKeys.map((key) => (
                  <div key={key} className="status-detail-item">
                    <span className="detail-label">{formatKey(key)}:</span>
                    {key.toLowerCase() === 'result' || key.toLowerCase() === 'status' ? (
                      <span className="detail-value" style={{ color: getStatusColor(String(obj[key])), fontWeight: 600 }}>{String(obj[key])}</span>
                    ) : (
                      <span className="detail-value">{String(obj[key])}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Response Body */}
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="section-icon">📄</span>
              <h3>Response Body</h3>
            </div>
            <div className="admin-payout-status-section-body">
              {responseBody ? (
                <div className="status-details-list response-body-details-grid">
                  {Object.entries(responseBody).map(([key, value]) => (
                    <div key={key} className="status-detail-item">
                      <span className="detail-label">{formatKey(key)}:</span>
                      {renderDetailValue(key, value)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data-message">No response body</div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-payout-status-footer">
          <button type="button" className="admin-payout-status-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}

const ReportManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'managers' | 'merchants' | 'refunds'>('managers');
  const [managerTransactions, setManagerTransactions] = useState<Transaction[]>([]);
  const [merchantTransactions, setMerchantTransactions] = useState<Payout[]>([]);
  const [merchantRefunds, setMerchantRefunds] = useState<Refund[]>([]);
  const [merchantSummary, setMerchantSummary] = useState({
    totalPayoutAmount: 0,
    totalPayoutCount: 0,
    totalServiceFee: 0,
  });
  const [refundSummary, setRefundSummary] = useState({
    totalRefundAmount: 0,
    totalRefundCount: 0,
  });
  const [loading, setLoading] = useState(false);
  
  // Pagination
  const [managersPage, setManagersPage] = useState(1);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [refundsPage, setRefundsPage] = useState(1);
  const [managersTotalPages, setManagersTotalPages] = useState(1);
  const [merchantsTotalPages, setMerchantsTotalPages] = useState(1);
  const [refundsTotalPages, setRefundsTotalPages] = useState(1);
  const [managersTotalRecords, setManagersTotalRecords] = useState(0);
  const limit = 20;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [payoutDetails, setPayoutDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Status Modal state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatusPayout, setSelectedStatusPayout] = useState<Payout | null>(null);
  const [statusDetails, setStatusDetails] = useState<any>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Response Message modal (Merchant Transfers - parsed data or plain string)
  const [responseMessageModalOpen, setResponseMessageModalOpen] = useState(false);
  const [responseMessageData, setResponseMessageData] = useState<string | object | null>(null);

  // Search and filters
  const [managersSearch, setManagersSearch] = useState('');
  const [merchantsSearch, setMerchantsSearch] = useState('');
  const [refundsSearch, setRefundsSearch] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'ALL' | 'CREDIT' | 'DEBIT'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PROCESSING' | 'SUCCESS' | 'FAILED'>('ALL');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchManagerTransactions();
  }, [managersPage, managersSearch, transactionTypeFilter, statusFilter, dateRange]);

  // Merchant payouts: heavy query (aggregate + joins). Only load when this tab is active
  // so opening Report Management on Manager Transfers is not blocked by a 10k-row scan.
  useEffect(() => {
    if (activeTab !== 'merchants') return;
    fetchMerchantTransactions();
  }, [activeTab, merchantsPage, merchantsSearch, transactionTypeFilter, statusFilter, dateRange]);

  useEffect(() => {
    if (activeTab === 'refunds') {
      fetchMerchantRefunds();
    }
  }, [activeTab, refundsPage, refundsSearch, dateRange]);

  const fetchManagerTransactions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTransactions({
        page: managersPage,
        limit,
        search: managersSearch,
        transaction_type: transactionTypeFilter === 'ALL' ? undefined : transactionTypeFilter,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        start_date: dateRange.startDate || undefined,
        end_date: dateRange.endDate || undefined,
        manager_sent_money: 'true' // Show only transactions where manager sent money
      });
      
      console.log('Manager API Response:', response.data);
      console.log('Manager transactions:', response.data.data.transactions);
      
      // Debug manager wallet balance
      if (response.data.data.transactions.length > 0) {
        const firstTransaction = response.data.data.transactions[0];
        console.log('🔍 First Transaction Debug:');
        console.log('  - ID:', firstTransaction.id);
        console.log('  - Created by:', firstTransaction.created_by_name, '(ID:', firstTransaction.created_by, ')');
        console.log('  - Manager wallet balance:', firstTransaction.manager_wallet_balance);
        console.log('  - Manager balance at transaction:', firstTransaction.manager_balance_at_transaction);
        console.log('  - Amount:', firstTransaction.amount);
        console.log('  - Transaction type:', firstTransaction.transaction_type);
      }
      
      setManagerTransactions(response.data.data.transactions);
      setManagersTotalPages(response.data.data.pagination.totalPages);
      setManagersTotalRecords(response.data.data.pagination.totalRecords);
    } catch (error) {
      console.error('Error fetching manager transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantTransactions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllPayouts({
        page: merchantsPage,
        limit,
        search: merchantsSearch,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        date_from: dateRange.startDate || undefined,
        date_to: dateRange.endDate || undefined
      });
      
      console.log('Payouts API Response:', response.data);
      console.log('Payouts:', response.data.data.payouts);
      setMerchantTransactions(response.data.data.payouts);
      setMerchantsTotalPages(response.data.data.pagination.totalPages);

      const s = response.data?.data?.summary;
      if (s) {
        setMerchantSummary({
          totalPayoutAmount: Number(s.totalPayoutAmount ?? 0),
          totalPayoutCount: Number(s.totalPayoutCount ?? 0),
          totalServiceFee: Number(s.totalServiceFee ?? 0),
        });
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchantRefunds = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getMerchantRefunds({
        page: refundsPage,
        limit,
        search: refundsSearch || undefined,
        date_from: dateRange.startDate || undefined,
        date_to: dateRange.endDate || undefined,
      });
      const data = response.data?.data;
      setMerchantRefunds(data?.refunds || []);
      setRefundsTotalPages(data?.pagination?.totalPages ?? 1);

      const s = data?.summary;
      if (s) {
        setRefundSummary({
          totalRefundAmount: Number(s.totalRefundAmount ?? 0),
          totalRefundCount: Number(s.totalRefundCount ?? 0),
        });
      }
    } catch (error) {
      console.error('Error fetching merchant refunds:', error);
      setMerchantRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionTypeIcon = (type: string) => {
    const upperType = type.toUpperCase();
    return (upperType === 'CREDIT' || upperType === 'WALLET_CREDIT' || upperType.includes('CREDIT')) ? '💰' : '💸';
  };

  const getTransactionTypeColor = (type: string) => {
    // Convert to uppercase for consistent comparison
    const upperType = type.toUpperCase();
    return (upperType === 'CREDIT' || upperType === 'WALLET_CREDIT' || upperType.includes('CREDIT')) ? 'credit' : 'debit';
  };

  const getTransactionDestination = (transaction: Transaction) => {
    // For manager transfers, the destination is the merchant who received the money
    // This is shown in the user_name and user_email fields
    if (transaction.user_name && transaction.user_email) {
      return `${transaction.user_name} (${transaction.user_email})`;
    }

    // Try to get destination from recipient_details or payment_details
    if (transaction.recipient_details) {
      try {
        const details = typeof transaction.recipient_details === 'string' 
          ? JSON.parse(transaction.recipient_details) 
          : transaction.recipient_details;
        
        if (details.name) {
          return `${details.name} (${details.email || details.id || 'Unknown'})`;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    if (transaction.payment_details) {
      try {
        const details = typeof transaction.payment_details === 'string' 
          ? JSON.parse(transaction.payment_details) 
          : transaction.payment_details;
        
        if (details.recipient_name) {
          return `${details.recipient_name} (${details.recipient_email || details.recipient_id || 'Unknown'})`;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    // Fallback based on transaction type and user role
    if (transaction.transaction_type === 'CREDIT') {
      if (transaction.user_role === 'manager') {
        return 'Manager Wallet';
      } else if (transaction.user_role === 'merchant') {
        return 'Merchant Wallet';
      }
    } else if (transaction.transaction_type === 'DEBIT') {
      if (transaction.user_role === 'manager') {
        return 'System/Commission';
      } else if (transaction.user_role === 'merchant') {
        return 'Payout/Transfer';
      }
    }

    return 'Unknown Destination';
  };

  const isPoolPayPayout = (payout: Payout): boolean =>
    String(payout.payout_source || '').toUpperCase().trim() === 'POOLPAY';

  const formatPayoutSource = (payout: Payout): string =>
    isPoolPayPayout(payout) ? 'PoolPay' : 'Viyona';

  const renderTransactionTable = (transactions: Transaction[] | Payout[], type: 'managers' | 'merchants') => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {type} transactions...</p>
        </div>
      );
    }

    if (transactions.length === 0) {
      return (
        <div className="empty-state">
          <span className="empty-icon">📊</span>
          <h3>No transactions found</h3>
          <p>No {type} transactions match your current filters</p>
        </div>
      );
    }

    // Enhanced view for Managers only
    if (type === 'managers') {
      const managerTransactions = transactions as Transaction[];
      return (
        <div className="table-container">
          <table className="transaction-table managers-table">
            <thead>
              <tr>
                <th>Serial No</th>
                <th>Date</th>
                <th>From Manager</th> 
                <th>Send To</th>
                <th>Manager Wallet Balance</th>
                <th>Amount Sent</th>
                <th>Merchant Wallet Balance</th> 
                <th>Transaction Type</th> 
                <th>Description</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {managerTransactions.map((transaction, index) => (
                <tr key={transaction.id}>
                  <td className="serial-number">{index + 1}</td>
                  <td className="date">{formatDate(transaction.created_at)}</td>
                  <td>
                    <div className="user-info">
                      <strong>{transaction.created_by_name || 'Unknown'}</strong>
                      <span className="user-email">{transaction.created_by_email || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="to-details">
                      {getTransactionDestination(transaction)}
                    </span>
                  </td>
                  <td className="wallet-balance">
                    <div className="balance-cell">
                      {(() => {
                        // Prioritize manager_wallet_balance since it has the correct value
                        const managerBalance = transaction.manager_wallet_balance || transaction.manager_balance_at_transaction || 0;
                        console.log(`🔍 Transaction ${transaction.id} Manager Balance:`, {
                          manager_balance_at_transaction: transaction.manager_balance_at_transaction,
                          manager_wallet_balance: transaction.manager_wallet_balance,
                          final_balance: managerBalance
                        });
                        return (
                          <>
                            <span className="balance-amount">{formatCurrency(managerBalance)}</span>
                            <span className="balance-label">Manager Wallet</span>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className={`amount ${getTransactionTypeColor(transaction.transaction_type)}-amount`}>
                    <span className="amount-symbol">{getTransactionTypeColor(transaction.transaction_type) === 'credit' ? '+' : '-'}</span>
                    <span className={`amount-text ${getTransactionTypeColor(transaction.transaction_type)}-text`}>
                      {formatCurrency(transaction.amount)}
                    </span>
                  </td>
                  <td className="wallet-balance">
                    <div className="balance-cell"> 
                     <span className="balance-amount">{formatCurrency(transaction.balance_before || 0)}</span>
                     <span className="balance-label">Before Transaction</span> 
                      <span className="balance-amount">{formatCurrency(transaction.balance_after || 0)}</span>
                      <span className="balance-label">After Transaction</span>
                    </div>
                  </td>
                  <td>
                    <span className={`transaction-type ${getTransactionTypeColor(transaction.transaction_type)}`}>
                      {getTransactionTypeIcon(transaction.transaction_type)} {transaction.transaction_type}
                    </span>
                  </td>
                  <td className="description">{transaction.description}</td>
                  <td>
                    <span className={`status-badge ${transaction.status.toLowerCase()}`}>
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Simple view for Merchants (Payouts)
    const payouts = transactions as Payout[];
    return (
      <div className="table-container">
        <table className="transaction-table merchants-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>MERCHANT NAME</th>
              <th>BENEFICIARY DETAILS</th>
              <th>AMOUNT</th>
              <th>CURRENT WALLET BALANCE</th>
              <th>TRANSFER TYPE</th>
              <th>MERCHANT REFERENCE NUMBER</th>
              <th>SOURCE</th>
              <th>STATUS</th>
              <th>RESPONSE MESSAGE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map(payout => (
              <tr key={payout.id}>
                <td className="date">{formatDate(payout.created_at)}</td>
                <td>
                  <div className="user-info">
                    <strong>{payout.user_name || 'N/A'}</strong>
                    <span className="user-email">{payout.user_email || 'N/A'}</span>
                  </div>
                </td>
                <td>
                  <div className="account-info">
                    <strong>{payout.account_name}</strong>
                    <span className="account-number">{payout.account_number}</span>
                    <span className="bank-name">{payout.bank_name}</span>
                  </div>
                </td>
                <td className="amount">{formatCurrency(payout.amount)}</td>
                <td className="wallet-balance">{formatCurrency(payout.current_wallet_balance || 0)}</td>
                <td>
                  <span className={`transfer-type ${payout.transfer_type.toLowerCase()}`}>
                    {payout.transfer_type}
                  </span>
                </td>
                <td>
                  <span className={`merchant-reference-number ${payout.merchant_reference_number.toLowerCase()}`}>
                    {payout.merchant_reference_number}
                  </span>
                </td>
                <td className="payout-source-cell">
                  <span className="payout-source-label">{formatPayoutSource(payout)}</span>
                </td>
                <td>
                  <span className={`status-badge ${payout.status.toLowerCase()}`}>
                    {payout.status}
                  </span>
                </td>
                <td>
                  {(payout.error_message || payout.response_data) ? (
                    <button
                      type="button"
                      className="btn-see-message"
                      onClick={() => {
                        const raw = payout.response_data;
                        if (raw != null && raw !== '') {
                          if (typeof raw === 'string') {
                            try {
                              setResponseMessageData(JSON.parse(raw));
                            } catch {
                              setResponseMessageData(raw);
                            }
                          } else {
                            setResponseMessageData(raw);
                          }
                        } else {
                          setResponseMessageData(payout.error_message ?? null);
                        }
                        setResponseMessageModalOpen(true);
                      }}
                    >
                      See Message
                    </button>
                  ) : (
                    <span className="error-message-cell">-</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon btn-info"
                      onClick={() => handleViewDetails(payout)}
                      title="View Details"
                    >
                      👁️
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-warning btn-live-status"
                      onClick={() => handleCheckStatus(payout)}
                      title={
                        isPoolPayPayout(payout)
                          ? 'Live Status (PoolPay)'
                          : 'Live Status (Viyona)'
                      }
                      aria-label={
                        isPoolPayPayout(payout)
                          ? 'Live Status (PoolPay)'
                          : 'Live Status (Viyona)'
                      }
                    >
                      <span className="live-status-dot" aria-hidden="true" />
                      <span className="live-status-label">Live Status</span>
                      <span className="live-status-magnifier" aria-hidden="true">🔍</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRefundsTable = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading merchant refunds...</p>
        </div>
      );
    }
    if (merchantRefunds.length === 0) {
      return (
        <div className="empty-state">
          <span className="empty-icon">↩️</span>
          <h3>No refunds found</h3>
          <p>No merchant refunds match your current filters</p>
        </div>
      );
    }
    return (
      <div className="table-container">
        <table className="transaction-table merchants-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>MERCHANT NAME</th>
              <th>AMOUNT</th>
              <th>PAYOUT REFERENCE</th>
              <th>DESCRIPTION</th>
            </tr>
          </thead>
          <tbody>
            {merchantRefunds.map((refund) => (
              <tr key={refund.id}>
                <td className="date">{formatDate(refund.created_at)}</td>
                <td>
                  <div className="user-info">
                    <strong>{refund.merchant_name || 'N/A'}</strong>
                    <span className="user-email">{refund.merchant_email || 'N/A'}</span>
                  </div>
                </td>
                <td className="amount">{formatCurrency(refund.amount)}</td>
                <td>
                  <span className="merchant-reference-number">
                    {refund.merchant_reference_number || refund.reference_id || '—'}
                  </span>
                </td>
                <td className="description">{refund.description || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPagination = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
    if (totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  const getTotalAmount = (transactions: Transaction[], type: 'CREDIT' | 'DEBIT') => {
    return transactions
      .filter(t => t.transaction_type === type)
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  };

  // Specific functions for each summary card - Manager Transfers only
  const getTotalCredit = (transactions: Transaction[]) => {
    // Manager credit transactions: wallet_credit and WALLET_CREDIT (manager sending money to merchants)
    const creditTransactions = transactions.filter(t => 
      t.transaction_type === 'wallet_credit' || t.transaction_type === 'WALLET_CREDIT'
    );
    const total = creditTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalCredit (Manager):', { transactionCount: creditTransactions.length, total, transactions: creditTransactions });
    return total;
  };

  const getTotalDebit = (transactions: Transaction[]) => {
    // Manager debit transactions: all wallet_debit, WALLET_DEBIT and DEBIT transactions
    const debitTransactions = transactions.filter(t => 
      t.transaction_type === 'DEBIT' || 
      t.transaction_type === 'WALLET_DEBIT' || 
      t.transaction_type === 'wallet_debit'
    );
    const total = debitTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalDebit (Manager):', { transactionCount: debitTransactions.length, total, transactions: debitTransactions });
    return total;
  };

  const getTotalDebitCommission = (transactions: Transaction[]) => {
    // Manager commission transactions: any debit with commission/fee
    const commissionTransactions = transactions.filter(t => 
      (t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit') && 
      (t.description.toLowerCase().includes('commission') || t.description.toLowerCase().includes('fee'))
    );
    const total = commissionTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalDebitCommission (Manager):', { transactionCount: commissionTransactions.length, total, transactions: commissionTransactions });
    return total;
  };

  const getTotalEscrowCredit = (transactions: Transaction[]) => {
    // Manager escrow credit transactions: escrow_release
    const escrowCreditTransactions = transactions.filter(t => 
      t.transaction_type === 'escrow_release'
    );
    const total = escrowCreditTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalEscrowCredit (Manager):', { transactionCount: escrowCreditTransactions.length, total, transactions: escrowCreditTransactions });
    return total;
  };

  const getTotalEscrowDebit = (transactions: Transaction[]) => {
    // Manager escrow debit transactions: any debit with escrow
    const escrowDebitTransactions = transactions.filter(t => 
      (t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit') && 
      t.description.toLowerCase().includes('escrow')
    );
    const total = escrowDebitTransactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);
    console.log('getTotalEscrowDebit (Manager):', { transactionCount: escrowDebitTransactions.length, total, transactions: escrowDebitTransactions });
    return total;
  };

  const getTotalServiceFee = (transactions: Transaction[]) => {
    // Total service fee: sum of all commission amounts
    const total = transactions.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0);
    console.log('getTotalServiceFee (Manager):', { transactionCount: transactions.length, total });
    return total;
  };

  // Manager transaction counts (dynamic summary cards)
  const getManagerCreditCount = (transactions: Transaction[]) => {
    return transactions.filter(t => t.transaction_type === 'wallet_credit' || t.transaction_type === 'WALLET_CREDIT').length;
  };

  const getManagerDebitCount = (transactions: Transaction[]) => {
    return transactions.filter(t => t.transaction_type === 'DEBIT' || t.transaction_type === 'WALLET_DEBIT' || t.transaction_type === 'wallet_debit').length;
  };

  // Merchant-specific calculation functions
  const getMerchantTotalCredit = (payouts: Payout[]) => {
    // Merchant payout amount: sum of all payout amounts
    const total = payouts.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    console.log('getMerchantTotalCredit (Payouts):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalDebit = (payouts: Payout[]) => {
    // Merchant payout count: count of payouts
    const total = payouts.length;
    console.log('getMerchantTotalDebit (Payout Count):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalDebitCommission = (payouts: Payout[]) => {
    // Merchant service fee: sum of transaction_fee_amount from payouts
    const total = payouts.reduce((sum, p) => sum + Number(p.transaction_fee_amount || 0), 0);
    console.log('getMerchantTotalDebitCommission (Service Fee):', { payoutCount: payouts.length, total, payouts });
    return total;
  };

  const getMerchantTotalServiceFee = (payouts: Payout[]) => {
    // Total service fee for merchants: sum of all transaction_fee_amount from payouts
    const total = payouts.reduce((sum, p) => sum + Number(p.transaction_fee_amount || 0), 0);
    console.log('getMerchantTotalServiceFee:', { payoutCount: payouts.length, total });
    return total;
  };

  const getTotalTransactions = (transactions: Transaction[]) => {
    return transactions.length;
  };

  const getTotalPayouts = (payouts: Payout[]) => {
    return payouts.length;
  };

  // Function to fetch payout details
  const fetchPayoutDetails = async (merchantReferenceNumber: string) => {
    try {
      setLoadingDetails(true);
      console.log('Fetching details for merchant reference:', merchantReferenceNumber);
      
      // Make API call to get payout details
      const response = await adminAPI.getPayoutDetails(merchantReferenceNumber);
      console.log('Payout details response:', response.data);
      
      setPayoutDetails(response.data);
    } catch (error) {
      console.error('Error fetching payout details:', error);
      setPayoutDetails({ error: 'Failed to fetch payout details' });
    } finally {
      setLoadingDetails(false);
    }
  };

  /** PoolPay → PoolPay enquiry; Viyona → admin Viyona fund-transfer status (separate APIs). */
  const fetchPayoutStatusDetails = async (payout: Payout) => {
    const merchantReferenceNumber = payout.merchant_reference_number;
    const poolPay = isPoolPayPayout(payout);

    try {
      setLoadingStatus(true);

      if (poolPay) {
        console.log('Fetching PoolPay live status for reference:', merchantReferenceNumber);
        const response = await adminAPI.checkPoolPayPayoutStatus(merchantReferenceNumber);
        console.log('PoolPay status response:', response.data);
        if (response.data && response.data.success) {
          setStatusDetails(response.data);
        } else {
          setStatusDetails({ error: response.data?.message || 'Failed to fetch PoolPay status' });
        }
        return;
      }

      console.log('Fetching Viyona live status for reference:', merchantReferenceNumber);
      const response = await adminAPI.checkPayoutStatus(merchantReferenceNumber);
      console.log('Viyona status response:', response.data);
      if (response.data && response.data.success) {
        setStatusDetails(response.data);
      } else {
        setStatusDetails({ error: response.data?.message || 'Failed to fetch Viyona status' });
      }
    } catch (error: any) {
      const label = poolPay ? 'PoolPay' : 'Viyona';
      console.error(`Error fetching ${label} status:`, error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);

      let errorMessage = `Failed to fetch live status from ${label}`;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatusDetails({ error: errorMessage });
    } finally {
      setLoadingStatus(false);
    }
  };

  // Function to open modal and fetch details
  const handleViewDetails = (payout: Payout) => {
    setSelectedPayout(payout);
    setIsModalOpen(true);
    fetchPayoutDetails(payout.merchant_reference_number);
  };

  const handleCheckStatus = (payout: Payout) => {
    setSelectedStatusPayout(payout);
    setIsStatusModalOpen(true);
    fetchPayoutStatusDetails(payout);
  };

  // Function to close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPayout(null);
    setPayoutDetails(null);
  };

  // Function to close status modal
  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedStatusPayout(null);
    setStatusDetails(null);
  };

  return (
    <div className="report-management">
      <div className="page-header">
        <div className="header-content">
          <h1>📊 Report Management</h1>
          <p>View Manager money transfers and Merchant transactions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'managers' ? 'active' : ''}`}
          onClick={() => setActiveTab('managers')}
        >
          <span className="tab-icon">👔</span>
          <span className="tab-label">Manager Transfers</span>
          <span className="tab-count">{managersTotalRecords}</span>
        </button>
        <button
          className={`tab ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <span className="tab-icon">🏪</span>
          <span className="tab-label">Merchant Transfers</span>
          <span className="tab-count" title={activeTab !== 'merchants' ? 'Open tab to load count' : undefined}>
            {activeTab === 'merchants' || merchantSummary.totalPayoutCount > 0
              ? merchantSummary.totalPayoutCount
              : '—'}
          </span>
        </button>
        <button
          className={`tab ${activeTab === 'refunds' ? 'active' : ''}`}
          onClick={() => setActiveTab('refunds')}
        >
          <span className="tab-icon">↩️</span>
          <span className="tab-label">Merchant Refunds</span>
          <span className="tab-count">{refundSummary.totalRefundCount}</span>
        </button>
      </div>

      {/* Section Header */}
      <div>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.9)' }}>
          {activeTab === 'managers' ? 'Manager Transfers' : activeTab === 'merchants' ? 'Merchant Transfers' : 'Merchant Refunds'}
        </h2>
        <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)' }}>
          {activeTab === 'managers' 
            ? 'View all money transfers initiated by managers to merchants' 
            : activeTab === 'merchants'
            ? 'View all merchant payout transactions'
            : 'View all merchant refunds (refunds for failed payouts)'
          }
        </p>
      </div>

      {/* Summary Cards - Merchant Transfers */}
      {activeTab === 'merchants' && (
        <div className="summary-cards">
          <div className="summary-card credit">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>TOTAL PAYOUT AMOUNT</h3>
              <p className="card-value">{formatCurrency(merchantSummary.totalPayoutAmount)}</p>
            </div>
          </div>
          <div className="summary-card credit">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>TOTAL PAYOUT COUNT</h3>
              <p className="card-value">{merchantSummary.totalPayoutCount}</p>
            </div>
          </div>
          <div className="summary-card credit"> 
            <div className="card-icon">💸</div>
            <div className="card-content">
              <h3>TOTAL SERVICE FEE</h3> 
              <p className="card-value">{formatCurrency(merchantSummary.totalServiceFee)}</p>
            </div> 
          </div>
        </div>
      )}

      {/* Summary Cards - Merchant Refunds */}
      {activeTab === 'refunds' && (
        <div className="summary-cards">
          <div className="summary-card credit">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>TOTAL REFUND AMOUNT</h3>
              <p className="card-value">{formatCurrency(refundSummary.totalRefundAmount)}</p>
            </div>
          </div>
          <div className="summary-card credit">
            <div className="card-icon">↩️</div>
            <div className="card-content">
              <h3>TOTAL REFUND COUNT</h3>
              <p className="card-value">{refundSummary.totalRefundCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          {activeTab === 'managers' && (
            <div className="filter-group">
              <label>Transaction Type:</label>
              <select
                value={transactionTypeFilter}
                onChange={(e) => setTransactionTypeFilter(e.target.value as 'ALL' | 'CREDIT' | 'DEBIT')}
                className="filter-select"
              >
                <option value="ALL">All Transactions</option>
                <option value="CREDIT">Credits Only</option>
                <option value="DEBIT">Debits Only</option>
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'PROCESSING' | 'SUCCESS' | 'FAILED')}
              className="filter-select"
            >
              <option value="ALL">All Status</option>
              <option value="PROCESSING">PROCESSING</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="filter-input"
            />
          </div>

          <button
            className="btn-secondary"
            onClick={() => {
              setDateRange({ startDate: '', endDate: '' });
              if (activeTab === 'managers') {
                setTransactionTypeFilter('ALL');
              }
              setStatusFilter('ALL');
              if (activeTab === 'refunds') {
                setRefundsSearch('');
                setRefundsPage(1);
              }
            }}
          >
            Clear Filters
          </button>
          
          {/* Search Input */}
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              className="search-input"
              placeholder={
                activeTab === 'managers'
                  ? 'Search manager transactions...'
                  : activeTab === 'merchants'
                  ? 'Search merchants transactions...'
                  : 'Search merchant refunds...'
              }
              value={
                activeTab === 'managers'
                  ? managersSearch
                  : activeTab === 'merchants'
                  ? merchantsSearch
                  : refundsSearch
              }
              onChange={(e) => {
                if (activeTab === 'managers') {
                  setManagersSearch(e.target.value);
                  setManagersPage(1);
                } else if (activeTab === 'merchants') {
                  setMerchantsSearch(e.target.value);
                  setMerchantsPage(1);
                } else {
                  setRefundsSearch(e.target.value);
                  setRefundsPage(1);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {activeTab === 'managers' && (
        <div className="summary-cards">
          <div className="summary-card credit">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>TOTAL CREDIT</h3>
              <p className="card-value">{formatCurrency(getTotalCredit(managerTransactions))}</p>
             
            </div>
          </div>
          <div className="summary-card debit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL DEBIT</h3>
              <p className="card-value">{formatCurrency(getTotalDebit(managerTransactions))}</p>
             
            </div>
          </div>

          <div className="summary-card credit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL CREDIT TRANSACTIONS</h3>
              <p className="card-value">{getManagerCreditCount(managerTransactions)}</p>
            </div> 
          </div>

           <div className="summary-card credit">
            <div className="card-icon">💸</div>
            <div className="card-content">  
              <h3>TOTAL DEBIT TRANSACTIONS</h3>
              <p className="card-value">{getManagerDebitCount(managerTransactions)}</p>
            </div> 
          </div>  
          
          
        </div>
      )}


      {/* Transaction Tables */}
      {activeTab === 'managers' && (
        <>
          {renderTransactionTable(managerTransactions, 'managers')}
          {renderPagination(managersPage, managersTotalPages, setManagersPage)}
        </>
      )}

      {activeTab === 'merchants' && (
        <>
          {renderTransactionTable(merchantTransactions, 'merchants')}
          {renderPagination(merchantsPage, merchantsTotalPages, setMerchantsPage)}
        </>
      )}

      {activeTab === 'refunds' && (
        <>
          {renderRefundsTable()}
          {renderPagination(refundsPage, refundsTotalPages, setRefundsPage)}
        </>
      )}

      {/* Payout Details Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payout Details</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              {loadingDetails ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading payout details...</p>
                </div>
              ) : payoutDetails ? (
                <div className="payout-details">
                  {payoutDetails.error ? (
                    <div className="error-message">
                      <p>❌ {payoutDetails.error}</p>
                    </div>
                  ) : (
                    <div className="details-grid">
                      <div className="detail-item">
                        <label>Merchant Reference:</label>
                        <span>{selectedPayout?.merchant_reference_number}</span>
                      </div>
                      <div className="detail-item">
                        <label>Amount:</label>
                        <span>{formatCurrency(selectedPayout?.amount || 0)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Status:</label>
                        <span className={`status-badge ${selectedPayout?.status.toLowerCase()}`}>
                          {selectedPayout?.status}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Account Name:</label>
                        <span>{selectedPayout?.account_name}</span>
                      </div>
                      <div className="detail-item">
                        <label>Account Number:</label>
                        <span>{selectedPayout?.account_number}</span>
                      </div>
                      <div className="detail-item">
                        <label>IFSC Code:</label>
                        <span>{selectedPayout?.ifsc_code}</span>
                      </div>
                      <div className="detail-item">
                        <label>Bank Name:</label>
                        <span>{selectedPayout?.bank_name}</span>
                      </div>
                      <div className="detail-item">
                        <label>Transfer Type:</label>
                        <span>{selectedPayout?.transfer_type}</span>
                      </div>
                      <div className="detail-item">
                        <label>Created At:</label>
                        <span>{formatDate(selectedPayout?.created_at || '')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Payout Status Modal */}
      <AdminPayoutStatusModal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        statusData={statusDetails}
        loading={loadingStatus}
        source={
          selectedStatusPayout != null && isPoolPayPayout(selectedStatusPayout) ? 'poolpay' : 'viyona'
        }
      />

      {/* Response Message Modal (Merchant Transfers) - same design as Payout Status */}
      {responseMessageModalOpen && (
        <ResponseMessageModalContent
          data={responseMessageData}
          onClose={() => setResponseMessageModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ReportManagement;
